import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CardBody,
  Row,
  Col,
  Label,
  Input,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { ToastContainer } from 'react-toastify';
import useTenderEvaluationTabVisibility from '../hooks/useTenderEvaluationTabVisibility';
import useTenderEvaluationList from '../hooks/useTenderEvaluationList';
import { getTenderEvaluationBadgeColor } from '../utils/tenderEvaluationHelpers';

const EVALUATION_STATUS_META = {
  'Ready for Evaluation': { color: 'primary' },
  'Under Evaluation': { color: 'success' },
  'Awaiting Evaluation': { color: 'danger' },
};

const StatusPill = ({ wording, metaMap, fallbackColor = 'secondary' }) => {
  if (!wording) return <span className='text-muted'>-</span>;

  const meta = metaMap[wording] || {};
  const color =
    meta.color || getTenderEvaluationBadgeColor(wording) || fallbackColor;

  return (
    <Badge
      color={color}
      className='px-2 py-1 fs-12 fw-medium rounded-pill text-nowrap'
      title={wording}
    >
      {wording}
    </Badge>
  );
};

const TenderEvalTable = ({ procurementTypeHeader, refreshTrigger = 0 }) => {
  const navigate = useNavigate();
  const { canViewProcurementTypeHeader } = useTenderEvaluationTabVisibility();
  const canViewTable = canViewProcurementTypeHeader(procurementTypeHeader);

  const {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useTenderEvaluationList({
    procurementTypeHeader,
    refreshTrigger,
    enabled: canViewTable,
  });

  if (!canViewTable) {
    return (
      <CardBody className='pt-0'>
        <p className='text-muted small mb-0 py-4 text-center'>
          You do not have access to view this procurement category.
        </p>
      </CardBody>
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const MobileCard = ({ item }) => (
    <div className='card mb-3 d-md-none border shadow-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <span className='text-primary fw-semibold fs-13'>{item.idProc}</span>
          <StatusPill
            wording={item.evaluationStatusWording}
            metaMap={EVALUATION_STATUS_META}
          />
        </div>

        <h6 className='card-title fw-bold mb-1 text-dark lh-base'>
          {item.title}
        </h6>
        <p className='text-muted fs-12 mb-2'>
          {item.productType?.main} <span className='mx-1'>•</span>{' '}
          {item.productType?.sub}
        </p>

        <div className='d-flex flex-wrap gap-3 mb-3 pb-3'>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Proc Type
            </span>
            <span className='fs-12 fw-medium text-dark'>{item.procType}</span>
          </div>
          <div>
            <span className='text-muted fs-11 text-uppercase d-block mb-1'>
              Description
            </span>
            <span
              className='fs-12 fw-medium text-dark text-truncate d-block'
              style={{ maxWidth: '200px' }}
            >
              {item.description || '-'}
            </span>
          </div>
        </div>

        <Button
          color='light'
          size='sm'
          className='w-100 border bg-white shadow-none text-dark fw-medium py-2'
          onClick={() => navigate(`/apps-tenderevaluation/dtl/${item.id}`)}
        >
          <i className='ri-eye-line align-bottom me-1 text-muted'></i> View
          Detail
        </Button>
      </div>
    </div>
  );

  const columns = [
    {
      id: 'idProc',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>ID Proc</span>
      ),
      selector: row => row.idProc,
      cell: row => <span className='text-dark'>{row.idProc}</span>,
      width: '130px',
      sortable: true,
    },
    {
      id: 'title',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Title</span>
      ),
      selector: row => row.title,
      cell: row => (
        <span className='text-dark text-truncate' title={row.title}>
          {row.title}
        </span>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'procType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Proc Type
        </span>
      ),
      selector: row => row.procType,
      cell: row => <span className='text-dark'>{row.procType}</span>,
      width: '180px',
      sortable: true,
    },
    {
      id: 'productType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Product Type
        </span>
      ),
      selector: row => row.productType?.main,
      cell: row => (
        <div className='d-flex flex-column justify-content-center py-2'>
          <span className='fw-medium text-dark'>{row.productType?.main}</span>
          <span className='text-muted small' style={{ fontSize: '11px' }}>
            {row.productType?.sub}
          </span>
        </div>
      ),
      minWidth: '180px',
      sortable: true,
    },
    {
      id: 'description',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Description
        </span>
      ),
      selector: row => row.description,
      cell: row => (
        <span className='text-dark text-truncate' title={row.description}>
          {row.description || '-'}
        </span>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'status',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Status</span>
      ),
      selector: row => row.evaluationStatusWording,
      cell: row => (
        <StatusPill
          wording={row.evaluationStatusWording}
          metaMap={EVALUATION_STATUS_META}
        />
      ),
      width: '200px',
      sortable: true,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted text-center w-100'>
          Action
        </span>
      ),
      cell: row => (
        <div className='text-center w-100'>
          <button
            className='btn btn-soft-primary btn-sm btn-icon'
            onClick={() => navigate(`/apps-tenderevaluation/dtl/${row.id}`)}
            title='Detail'
          >
            <i className='ri-eye-line align-bottom'></i>
          </button>
        </div>
      ),
      width: '90px',
      center: true,
      sortable: false,
    },
  ];

  return (
    <>
      <ToastContainer closeButton={false} />
      <CardBody className='pt-0'>
        <Row className='mb-3'>
          <Col xs={12}>
            <Row className='g-3'>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Search</Label>
                <Input
                  type='text'
                  placeholder='Search by title or procurement no...'
                  value={searchText}
                  onChange={handleSearch}
                  className='form-control'
                />
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Label className='form-label'>Status</Label>
                <Input
                  type='select'
                  value={statusFilter}
                  onChange={e => handleStatusFilter(e.target.value)}
                  className='form-control'
                >
                  <option value=''>All Status</option>
                  <option value='Requested'>Ready for Evaluation</option>
                  <option value='Submitted'>Awaiting Evaluation</option>
                  <option value='UnderEvaluation'>Under Evaluation</option>
                </Input>
              </Col>
              <Col xs={12} md={6} lg={3} className='d-flex align-items-end'>
                <Button
                  color='danger'
                  outline
                  onClick={clearFilters}
                  className='w-100'
                >
                  <i className='ri-refresh-line me-1'></i>
                  <span className='d-none d-sm-inline'>Clear Filters</span>
                  <span className='d-sm-none'>Clear</span>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className='mb-2'>
          <Col xs={12}>
            <small className='text-muted'>
              {isLoading
                ? 'Loading...'
                : `Showing ${startEntry} to ${endEntry} of ${totalRows} entries`}
            </small>
          </Col>
        </Row>

        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner color='primary' />
            <p className='text-muted small mt-2 mb-0'>
              Loading tender evaluation data...
            </p>
          </div>
        ) : (
          <>
            <div className='d-md-none mt-3'>
              {data.items?.length > 0 ? (
                data.items.map((item, index) => (
                  <MobileCard key={item.id || index} item={item} />
                ))
              ) : (
                <div className='table-empty text-center py-4'>
                  <i
                    className='ri-inbox-line text-muted'
                    style={{ fontSize: '3rem', opacity: 0.5 }}
                  ></i>
                  <p className='mt-2 text-muted'>No procurement data found</p>
                </div>
              )}
            </div>

            <div className='d-none d-md-block'>
              <div
                className='table-responsive'
                style={{ maxHeight: '600px', overflowY: 'auto' }}
              >
                <DataTable
                  columns={columns}
                  data={data.items}
                  pagination={false}
                  onSort={handleSort}
                  sortServer
                  defaultSortFieldId='idProc'
                  defaultSortAsc={false}
                  highlightOnHover
                  responsive
                  striped
                  persistTableHead
                  dense
                  customStyles={{
                    header: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        color: '#333',
                        fontWeight: 'bold',
                        minHeight: '40px',
                      },
                    },
                    headCells: {
                      style: {
                        backgroundColor: '#f0f0f0',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                      },
                    },
                    cells: {
                      style: {
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        wordBreak: 'break-word',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                      },
                    },
                    rows: { style: { minHeight: '45px' } },
                    table: { style: { width: '100%' } },
                    tableWrapper: { style: { display: 'table' } },
                  }}
                />
              </div>
            </div>
          </>
        )}

        <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
          <div className='d-flex align-items-center'>
            <span className='text-muted me-2'>Rows per page:</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className='d-flex align-items-center'>
            <span className='text-muted me-3'>
              {startEntry}-{endEntry} of {totalRows}
            </span>
            <div className='btn-group' role='group'>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(1)}
              >
                <i className='ri-skip-back-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage <= 1 || isLoading}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <i className='ri-arrow-left-s-line'></i>
              </button>
              <span className='btn btn-outline-secondary btn-sm disabled'>
                {currentPage}
              </span>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <i className='ri-arrow-right-s-line'></i>
              </button>
              <button
                type='button'
                className='btn btn-outline-secondary btn-sm'
                disabled={currentPage >= totalPages || isLoading}
                onClick={() => handlePageChange(totalPages)}
              >
                <i className='ri-skip-forward-line'></i>
              </button>
            </div>
          </div>
        </div>
      </CardBody>
    </>
  );
};

export default React.memo(TenderEvalTable);
